/**
 * Formula resolution helpers for the Export Report feature.
 *
 * These are NOT used in the live rendering path — the AGS data endpoints return
 * pre-resolved values, so client-side formula evaluation is redundant.
 *
 * Preserved here in case we migrate away from AGS to our own demographics data
 * storage, where formula resolution would need to happen on the client again.
 */
import {
  evaluateFormula, evaluateFormulaLeftToRight,
} from '~/_shared/utils/number/evaluate.helpers';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import {
  DEFAULT_REPORT_ITEM_FORMAT_TYPE,
  type DemographicValue,
  formatReportValue, getTemplateRowFormatType, isReportFormulaPercentage,
  ReportItemFormatType,
} from '~/exportReport/formatting/exportReportFormatting.helpers';
import type { ExportReportTableValue } from '~/exportReport/table/ReportTable.model';
import type {
  ReportTemplate, ReportTemplateDataRow, ReportTemplateTableCell, ReportTemplateTableColumn,
} from '~/exportReport/template/reportTemplate';
import {
  getReportTemplateDataRowDemographicId,
  normalizeReportDemographicId, type ReportDemographicData,
} from '../exportReportData.helpers';

const REPORT_VALUE_NOT_AVAILABLE = {
  value: 'N/A',
  valueRaw: null,
  isError: true,
} as const;

const complexCalculations = ['SUM(', 'CLASS(', 'DIVERSITY(', 'MEDIAN(', 'APC(', 'LOOKUP(', 'PERCENTILE(', '~'];

const isReportFormulaIsTooComplex = (formula: string): boolean => {
  for (const calculation of complexCalculations) {
    if (formula.includes(calculation)) {
      return true;
    }
  }

  return false;
};

export const getFormulaDemographicIds = (formula: string): readonly string[] => {
  const cleaned = formula.replace(/%/g, '');
  const matches = cleaned.match(/[A-Z][A-Z0-9]*/gi) || [];
  return [...new Set(matches)];
};

export const getFormulaRowsById = (template: ReportTemplate): ReadonlyMap<string, ReportTemplateDataRow> => {
  return new Map(template.items.flatMap(item => item.type === 'table'
    ? item.content.flatMap(row => row.type === 'data-row' && row.formula ? [[row.id, row] as const] : [])
    : []));
};

type GetDemographicData = (columnId: string, demographicId: string) => ReportDemographicData | null;

const getFetchedDemographicValue = ({ columnId, demographicId, getDemographicData }: Readonly<{
  columnId: string;
  demographicId: string;
  getDemographicData: GetDemographicData;
}>): number | string | null => {
  const demographicData = getDemographicData(columnId, demographicId);
  if (!demographicData || demographicData.value === null) {
    return null;
  }

  return demographicData.value;
};

const getReportFormulaValue = (formula: string, getDemographicValue: (demographicId: string) => null | DemographicValue): number | null => {
  const lookup: Record<string, number> = {};
  const formulaDemographics = getFormulaDemographicIds(formula);
  for (const id of formulaDemographics) {
    try {
      const value = Number(getDemographicValue(id));
      if (value === null || isNaN(value)) {
        return null;
      }
      lookup[id] = value;
    }
    catch (e) {
      return null;
    }
  }

  let result = evaluateFormulaLeftToRight(formula.replace(/%$/, ''), lookup, 6);
  if (result === null) {
    return null;
  }
  else {
    if (formula.endsWith('%')) {
      result = result * 100;
    }

    return result;
  }
};

const getReferencedFormulaRowValue = ({ columnId, formulaRow, getDemographicData }: Readonly<{
  columnId: string;
  formulaRow: ReportTemplateDataRow | undefined;
  getDemographicData: GetDemographicData;
}>): DemographicValue | null => {
  if (!formulaRow?.formula || isReportFormulaIsTooComplex(formulaRow.formula)) {
    return null;
  }

  const value = getReportFormulaValue(formulaRow.formula, demographicId => getFetchedDemographicValue({
    columnId,
    demographicId,
    getDemographicData,
  }));

  return isNullsy(value) ? null : value;
};

const getTemplateDemographicValue = ({ columnId, demographicId, formulaRowsById, getDemographicData }: Readonly<{
  columnId: string;
  demographicId: string;
  formulaRowsById: ReadonlyMap<string, ReportTemplateDataRow>;
  getDemographicData: GetDemographicData;
}>): DemographicValue | null => {
  return getFetchedDemographicValue({ columnId, demographicId, getDemographicData })
    ?? getReferencedFormulaRowValue({ columnId, formulaRow: formulaRowsById?.get(normalizeReportDemographicId(demographicId)), getDemographicData });
};

export const getFormulaTableCellValue = ({
  columnId, formula, templateRow, getDemographicData, formulaRowsById,
}: Readonly<{
  columnId: string;
  formula: string;
  templateRow: ReportTemplateDataRow;
  getDemographicData: GetDemographicData;
  formulaRowsById: ReadonlyMap<string, ReportTemplateDataRow>;
}>): ExportReportTableValue => {
  const formulaDemographicIds = getFormulaDemographicIds(formula);

  const getValue = (demographicId: string) =>
    getTemplateDemographicValue({ columnId, demographicId, formulaRowsById, getDemographicData });

  if (formulaDemographicIds.some(demographicId => !getValue(demographicId))) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const value = getReportFormulaValue(formula, getValue);
  const unit = templateRow.unit ?? '';

  const formatType = formula.endsWith('%')
    ? ReportItemFormatType.PERCENT
    : templateRow.format_type
      ? getTemplateRowFormatType(templateRow.format_type)
      : DEFAULT_REPORT_ITEM_FORMAT_TYPE;

  return {
    value: formatReportValue({ value, formatType, unit }),
    valueRaw: value,
    isError: isNullsy(value),
  };
};

const normalizeFormulaKey = (value: string): string => value.replace(/_/g, '');

export const getExportReportFormatType = ({ column, cell, formula }: {
  column?: ReportTemplateTableColumn;
  cell?: ReportTemplateTableCell;
  formula: string;
}) => {
  const formatTypeRaw = column?.format_type ?? cell?.format_type;

  if (isReportFormulaPercentage(formula)) {
    return ReportItemFormatType.PERCENT;
  }

  return formatTypeRaw ? getTemplateRowFormatType(formatTypeRaw) : DEFAULT_REPORT_ITEM_FORMAT_TYPE;
};

export const prepareExportReportFormulaVariables = ({
  dataSourceColumnId,
  getDemographicData,
  columns,
  cells,
}: {
  dataSourceColumnId: string;
  getDemographicData: GetDemographicData;
  columns: ReadonlyArray<ReportTemplateTableColumn>;
  cells: ReadonlyArray<ReportTemplateTableCell>;
}) => {
  const variables: Record<string, number> = Object.fromEntries(columns.map((column, index) => {
    if (column.formula) {
      return null;
    }

    const cell = cells[index];
    if (!cell) {
      return null;
    }

    const rawValue = getFetchedDemographicValue({
      columnId: dataSourceColumnId,
      demographicId: getReportTemplateDataRowDemographicId(cell),
      getDemographicData,
    });
    const numericValue = Number(rawValue);
    if (rawValue === null || isNaN(numericValue)) {
      return null;
    }

    return [normalizeFormulaKey(column.id), numericValue];
  }).filter(notNullsy));

  return variables;
};

export const getReportTableColumnFormulaValue = ({
  formula, formatType, variables, unit,
}: Readonly<{
  formula: string;
  formatType: ReportItemFormatType;
  variables: Record<string, number>;
  unit: string;
}>): ExportReportTableValue => {
  const isPercentage = isReportFormulaPercentage(formula);
  const evaluated = evaluateFormula(normalizeFormulaKey(formula.replace(/%\s*$/, '')), variables, 6);
  const value = evaluated === null || !isPercentage ? evaluated : evaluated * 100;

  return {
    value: formatReportValue({ value, formatType, unit }),
    valueRaw: value,
  };
};
